import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Field } from 'formik';
import { useIntl } from 'react-intl';
import { styles } from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { FormInput, FormSelect } from '../../../Molecules';
import { countryApi } from '../../../../httpclient/index';
import FormCheckbox from '../../../Molecules/FormCheckbox';
import { formatCountryOptions } from '../../../../helpers/formattingHelpers';

interface OptionalShipmentAddressProps {
  optional?: boolean;
}

export const OptionalShipmentAddress: React.FC<OptionalShipmentAddressProps> = ({
  optional = true,
}) => {
  const intl = useIntl();
  const [checked, setChecked] = useState(!optional);

  const { result: countryResults, loading: countryLoading } = useApi({
    apiFunction: () => countryApi.v2CountryGetallcountriesv2Get(),
    callApiOnInit: true,
  });

  const countryOptions = useMemo(() => {
    return formatCountryOptions(countryResults, true);
  }, [countryResults]);

  return (
    <>
      {optional && (
        <>
          <CheckboxWrapper
            name="checked"
            component={FormCheckbox}
            title={intl.formatMessage({ id: 'contactinfo.differentaddress' })}
            onChange={setChecked}
            value={checked}
            type="checkbox"
            data-testid="checkBox"
          />
          <br />
        </>
      )}
      {checked && (
        <OptionalAddressFields optional={optional}>
          <NameSubHeader className="OptionalAddressHeader">
            {intl.formatMessage({ id: 'contactinfo.deliveryaddress' })}
          </NameSubHeader>
          <FieldWrapper
            name="receiver"
            component={FormInput}
            label={intl.formatMessage({ id: 'contactinfo.receiver' })}
            type="text"
          />
          <FieldWrapper
            name="address"
            component={FormInput}
            label={intl.formatMessage({ id: 'contactinfo.streetaddress' })}
            type="text"
          />
          <FieldWrapper
            name="address2"
            component={FormInput}
            label={intl.formatMessage({ id: 'contactinfo.streetaddress.optional' })}
            type="text"
          />
          <PostFieldWrapper>
            <Field
              name="zip"
              component={FormInput}
              label={intl.formatMessage({ id: 'contactinfo.postnr' })}
              type="text"
            />
            <Field
              name="city"
              component={FormInput}
              label={intl.formatMessage({ id: 'contactinfo.postlocation' })}
              type="text"
            />
          </PostFieldWrapper>
          <FieldWrapper
            name="country"
            component={FormSelect}
            label={
              countryLoading
                ? intl.formatMessage({ id: 'contactinfo.loading' })
                : intl.formatMessage({ id: 'contactinfo.country' })
            }
            type="options"
            searchable
            options={countryOptions}
          />
        </OptionalAddressFields>
      )}
    </>
  );
};

const CheckboxWrapper = styled(Field)`
  margin-bottom: 1em;
`;

const OptionalAddressFields = styled.div<{ optional: boolean }>`
  ${({ optional }) => optional && 'margin-top: 2em;'}
  .OptionalAddressHeader {
    margin-bottom: 1.5em;
  }
`;

const PostFieldWrapper = styled.div`
  display: flex;
  grid-column-gap: 1em;
  width: 20em;
  margin-bottom: 15px;
  * {
    max-width: 9.5em;
  }
`;

const FieldWrapper = styled(Field)`
  width: 20em;
  margin-bottom: 15px;
`;

const BoldText = styled.p`
  font-weight: bold;
  margin-top: auto;
`;

const NameSubHeader = styled(BoldText)`
  font-size: 1.75rem;
  color: ${styles.colors.font.textTable};
  margin-bottom: 0.5rem;
`;
